// Phase G — Upgrade modal.
//
// Mounted globally inside AppShell. Surfaces when usePendingUpgradeTrigger
// returns a fresh, un-converted, un-dismissed conversion trigger of an
// upgrade-relevant kind. "Maybe later" persists a dismissed_at into
// trigger_meta via the SECURITY DEFINER RPC dismiss_conversion_trigger.
// "Upgrade" routes to /pricing.
//
// Doctrine: never fabricate. The copy is keyed off trigger_kind; if a
// new kind ships without a matching headline we fall back to a
// neutral generic line rather than inventing a specific narrative.
//
// Phase H — CTA routes through the Paystack flow. When subscriptions
// aren't yet configured the helper returns "not_configured" and the
// modal falls through to /pricing so the user still has a destination.
// When there's no auth session we push to /auth?intent=signup&plan=…
// to capture intent + the chosen plan for post-signup checkout.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { initiatePaystackCheckout } from "../billing/initiatePaystackCheckout.ts";
import { usePlanForTier } from "../billing/usePlanForTier.ts";
import { trackEvent } from "../conversion/trackEvent.ts";
import {
  usePendingUpgradeTrigger,
  type PendingTrigger,
} from "../free-tier/usePendingUpgradeTrigger.ts";

interface ModalCopy {
  headline: string;
  body: string;
}

function copyForTrigger(trigger: PendingTrigger): ModalCopy {
  switch (trigger.trigger_kind) {
    case "free_acca_won_3_in_row":
      return {
        headline: "You've seen us land 3 days running.",
        body:
          "Three Daily Accas in a row landed. STRIKZA Pro unlocks the Super Acca, " +
          "the Bookmaker Matrix, and the Bankroll Advisor.",
      };
    case "super_acca_view_count_5":
      return {
        headline: "You've checked Super Acca 5 days running.",
        body:
          "You're already part of the routine. Pro unlocks today's slip in full, " +
          "plus historical receipts, ladder, and Sika.",
      };
    case "daily_acca_engaged_3_days":
      return {
        headline: "Three days back. We noticed.",
        body:
          "You've tracked the Daily Acca three days running. Pro adds the paid " +
          "Super Acca, Sika goal-planner, and Bankroll Advisor.",
      };
    case "sika_simulator_completed":
      return {
        headline: "You ran the goal plan. Want the real thing?",
        body:
          "Sika Pro turns the simulator into a live goal: ladder schedule, " +
          "rollover protection, and weekly receipts.",
      };
  }
}

export function UpgradeModal(): JSX.Element | null {
  const { trigger, dismiss } = usePendingUpgradeTrigger();
  const navigate = useNavigate();
  // The modal pitches the entry-tier (Sika Pro) regardless of trigger
  // kind — that's the conversion target.
  const { plan } = usePlanForTier("sika_pro");
  const [busy, setBusy] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  // paywall_seen — fire once when modal appears for a given trigger.
  useEffect(() => {
    if (trigger) {
      trackEvent("paywall_seen", { trigger_kind: trigger.trigger_kind });
    }
  }, [trigger?.id, trigger?.trigger_kind, trigger]);

  if (!trigger) return null;

  const copy = copyForTrigger(trigger);

  async function handleUpgrade(): Promise<void> {
    setNotice(null);
    if (!plan) {
      navigate("/pricing");
      return;
    }
    setBusy(true);
    const result = await initiatePaystackCheckout(plan.plan_code);
    setBusy(false);
    if (result.kind === "redirect") {
      window.location.href = result.authorization_url;
      return;
    }
    if (result.kind === "needs_auth") {
      navigate(`/auth?intent=signup&plan=${encodeURIComponent(plan.plan_code)}`);
      return;
    }
    if (result.kind === "not_configured") {
      // Graceful no-op — still give the user a destination.
      navigate("/pricing");
      return;
    }
    setNotice(result.message);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8, 8, 12, 0.66)",
        padding: 16,
      }}
      onClick={() => {
        void dismiss();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 460,
          width: "100%",
          background: "#0e0e10",
          color: "#fff",
          border: "1px solid #2a2a30",
          borderRadius: 16,
          padding: "28px 28px 22px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            fontFamily:
              "ui-monospace,Menlo,Consolas,monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "#b07cff",
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          STRIKZA · Upgrade
        </div>
        <h2
          id="upgrade-modal-title"
          style={{
            fontSize: 22,
            lineHeight: 1.25,
            margin: "0 0 10px",
            letterSpacing: "-0.01em",
          }}
        >
          {copy.headline}
        </h2>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "#c8cad0",
            margin: "0 0 22px",
          }}
        >
          {copy.body}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => {
              void dismiss();
            }}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #2a2a30",
              background: "transparent",
              color: "#c8cad0",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={() => {
              void handleUpgrade();
            }}
            disabled={busy}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              background: "#b07cff",
              color: "#0a0a0a",
              fontWeight: 700,
              fontSize: 13,
              border: "none",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Redirecting…" : "See Pro"}
          </button>
        </div>
        {notice ? (
          <div
            role="status"
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "#c8cad0",
              padding: "8px 10px",
              background: "rgba(176, 124, 255, 0.08)",
              borderRadius: 8,
            }}
          >
            {notice}{" "}
            <Link to="/pricing" style={{ color: "#b07cff" }}>
              See pricing
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
